import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'

import { C, E, clamp, lerp, mixColor, prog, spring } from '../../clip-geometry'
import { BORDER, BX, BY, SH, SW, blink, bounceOut, lookDir } from '../stage'

// Variante "Aivy Dots": vier Brand-Punkte als lebendiges Wesen.
// Sie fallen rein, huepfen in den Button und werden zum Gesicht, sobald die Maus naht.

const COLORS = [C.blue, C.green, C.yellow, C.pink]
const R = 5.4
const FLOOR = SH - 8
const START_X = [BX - 66, BX - 44, BX - 22, BX]
const FALL_DUR = 0.95
const HOP_DUR = 0.42
const INTRO_END = 3.2
const fallStart = (i) => 0.1 + i * 0.17
const hopStart = (i) => 1.55 + i * 0.07

const mix = (a, b, w) => ({
  x: lerp(a.x, b.x, w),
  y: lerp(a.y, b.y, w),
  sx: lerp(a.sx, b.sx, w),
  sy: lerp(a.sy, b.sy, w),
  rot: lerp(a.rot, b.rot, w)
})

// Gesicht: Blau und Gruen sind Augen, Gelb und Pink das Laecheln
const face = (i, look, smile) =>
  i < 2
    ? {
        x: (i ? 6.5 : -6.5) + look[0],
        y: -4 + look[1],
        sx: 0.82,
        sy: 1.25,
        rot: 0
      }
    : {
        x: (i === 2 ? -1 : 1) * (3.6 + smile * 1.2),
        y: 6.8,
        sx: 1.3 + smile * 0.15,
        sy: 0.62,
        rot: (i === 2 ? 1 : -1) * (24 + smile * 8)
      }

const row = (i, t, speed, amp) => ({
  x: (i - 1.5) * 10.5,
  y: -amp * Math.max(0, Math.sin(t * speed - i * 0.9)),
  sx: 0.92,
  sy: 0.92,
  rot: 0
})

function formation(i, t, ui) {
  const a = t * 0.7 + (i * Math.PI) / 2
  const r = 8.5 + 1.3 * Math.sin(t * 1.6 + i * 1.3)
  const idle = {
    x: Math.cos(a) * r,
    y: Math.sin(a) * r,
    sx: 1 + 0.08 * Math.sin(t * 2.2 + i),
    sy: 1 + 0.08 * Math.sin(t * 2.2 + i),
    rot: 0
  }
  const d = lookDir(ui)
  const lk = ui.open > 0.5 ? [0, -2] : [d[0] * 2 * ui.near, d[1] * 2 * ui.near]
  const wake = clamp(Math.max(ui.near * 1.25, ui.hover, ui.open))
  let f = mix(idle, face(i, lk, ui.hover), E.inOutCubic(wake))
  if (i < 2) f.sy *= lerp(1, blink(t, 4.2), wake)
  if (i < 2) f.sy *= lerp(1, 0.45, ui.open)
  const hello = prog(t, 2.05, 2.25) * (1 - prog(t, 2.85, 3.15))
  const think = Math.max(hello, ui.thinking)
  if (think > 0)
    f = mix(
      f,
      row(i, t, hello > ui.thinking ? 9 : 11, hello > ui.thinking ? 6 : 4.5),
      think
    )
  return f
}

function frame(t, ui, el) {
  const d = lookDir(ui)
  const mag = Math.min(5, Math.hypot(ui.px, ui.py) * 0.03) * ui.near
  const cx = BX + d[0] * mag
  const cy = BY + d[1] * mag

  const cr = 30 * spring(t - 1.5, 8, 14) * (1 + 0.05 * ui.hover)
  el.circle.setAttribute('cx', BX + d[0] * mag * 0.4)
  el.circle.setAttribute('cy', BY + d[1] * mag * 0.4)
  el.circle.setAttribute('r', Math.max(0, cr))
  el.circle.setAttribute('stroke', mixColor(BORDER, C.blue, ui.hover))

  el.dots.forEach((dot, i) => {
    const f = formation(i, t, ui)
    let x = cx + f.x
    let y = cy + f.y
    let { sx, sy, rot } = f
    const fs = fallStart(i)
    if (t < fs) {
      dot.style.opacity = 0
      return
    }
    dot.style.opacity = 1
    const hs = hopStart(i)
    if (t < hs) {
      // Fallen mit Squash & Stretch
      const p = prog(t, fs, fs + FALL_DUR)
      const yy = lerp(-20, FLOOR - R, bounceOut(p))
      const v = (bounceOut(clamp(p + 0.01)) - bounceOut(p)) / 0.01
      sy = 1 + clamp(Math.abs(v) * 0.09, 0, 0.32)
      sx = 1 / sy
      if (p > 0.02 && p < 1 && FLOOR - R - yy < 2.5) {
        sy = 0.66
        sx = 1.32
      }
      x = START_X[i]
      y = yy + R * (1 - sy)
      rot = 0
    } else if (t < hs + HOP_DUR) {
      // Sprung in den Button
      const p = prog(t, hs, hs + HOP_DUR)
      const e = E.inOutCubic(p)
      x = lerp(START_X[i], x, e)
      y = lerp(FLOOR - R, y, e) - 36 * 4 * p * (1 - p)
      sx = 0.88
      sy = 1.16
      rot = 0
    }
    dot.setAttribute(
      'transform',
      `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${rot.toFixed(1)}) scale(${sx.toFixed(3)} ${sy.toFixed(3)})`
    )
  })
}

const DotsStage = forwardRef(function DotsStage(_, ref) {
  const svgRef = useRef(null)
  const elRef = useRef(null)

  useLayoutEffect(() => {
    const svg = svgRef.current
    elRef.current = {
      circle: svg.querySelector('.ad-circle'),
      dots: Array.from(svg.querySelectorAll('.ad-dot'))
    }
  }, [])

  useImperativeHandle(ref, () => ({
    frame: (t, ui) => elRef.current && frame(t, ui, elRef.current)
  }))

  return (
    <svg
      ref={svgRef}
      width={SW}
      height={SH}
      viewBox={`0 0 ${SW} ${SH}`}
      className='absolute inset-0 overflow-visible'
      aria-hidden='true'
    >
      <circle
        className='ad-circle'
        cx={BX}
        cy={BY}
        r='0'
        fill='#fff'
        strokeWidth='1.5'
      />
      {COLORS.map((c, i) => (
        <ellipse
          key={i}
          className='ad-dot'
          rx={R}
          ry={R}
          fill={c}
          opacity='0'
        />
      ))}
    </svg>
  )
})

export function DotsAvatar({ size = 36 }) {
  const k = 2.4
  return (
    <svg width={size} height={size} viewBox='0 0 100 100' aria-hidden='true'>
      <circle
        cx='50'
        cy='50'
        r='48.5'
        fill='#fff'
        stroke={BORDER}
        strokeWidth='3'
      />
      {COLORS.map((c, i) => {
        const f = face(i, [0, 0], 0)
        return (
          <ellipse
            key={i}
            rx={R}
            ry={R}
            fill={c}
            transform={`translate(${50 + f.x * k} ${50 + f.y * k}) rotate(${f.rot}) scale(${f.sx * k} ${f.sy * k})`}
          />
        )
      })}
    </svg>
  )
}

DotsAvatar.propTypes = { size: PropTypes.number }

export default {
  id: 'dots',
  name: 'Aivy Dots',
  description:
    'Vier Brand-Punkte als lebendiges Wesen. Sie fallen rein, hüpfen in den Button und werden zum Gesicht, sobald die Maus näher kommt. Wenn der Assistent nachdenkt, laufen sie als Tipp-Welle.',
  introEnd: INTRO_END,
  Stage: DotsStage,
  Avatar: DotsAvatar
}
